/**
 * HTML to React-PDF Converter
 *
 * Converts sanitized HTML into React-PDF components
 * Supports a subset of HTML tags that map to React-PDF primitives
 */

import { Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import parse, { DOMNode, domToReact, Element } from "html-react-parser";
import * as React from "react";
import type { ReportData } from "../types";
import { RadarChartPdf } from "./radar-chart-pdf";

/**
 * Result of parsing styles with potential background image
 */
interface ParsedStyleResult {
  styles: Style;
  backgroundImage?: string;
}

/**
 * Style mapping from CSS to React-PDF StyleSheet
 */
type PDFStyles = Style;

/**
 * Represents a single page in the PDF
 */
export interface PageContent {
  /** React-PDF component for this page */
  component: React.ReactElement;
  /** Whether this is a fullpage background page (no padding) */
  isFullPage: boolean;
  /** Background image URL for fullpage pages */
  backgroundImage?: string;
}

/**
 * Conversion result
 */
export interface ConversionResult {
  /** React-PDF component tree (legacy, for non-fullpage content) */
  component: React.ReactElement | null;
  /** Errors encountered during conversion */
  errors: string[];
  /** Separated pages for multi-page documents with fullpage backgrounds */
  pages?: PageContent[];
}

/**
 * Component styles for placeholders
 */
const componentStyles = StyleSheet.create({
  placeholder: {
    padding: 10,
    marginVertical: 10,
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
    fontSize: 10,
    color: "#666"
  },
  tableContainer: {
    marginVertical: 10,
    borderWidth: 1,
    borderColor: "#333",
    width: "100%"
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    width: "100%"
  },
  tableHeader: {
    backgroundColor: "#e0e0e0"
  },
  tableCell: {
    width: "33%",
    padding: 8,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightColor: "#333"
  },
  tableCellLast: {
    width: "34%",
    padding: 8,
    fontSize: 10
  }
});

/**
 * Parse component type and optional size parameter
 * e.g., "RADAR:350" → { type: "RADAR", size: 350 }
 * e.g., "RADAR" → { type: "RADAR", size: undefined }
 * @param componentType
 */
function parseComponentType(componentType: string): { type: string; size?: number } {
  const parts = componentType.split(":");
  const type = parts[0] ?? componentType;
  const sizeString = parts[1];
  const size = sizeString ? Number.parseInt(sizeString, 10) : undefined;

  // Debug: log parsed component type
  console.log("[parseComponentType] Input:", componentType, "→ type:", type, ", size:", size);

  // Ensure size is valid (finite positive number)
  const validSize = size !== undefined && Number.isFinite(size) && size > 0 ? size : undefined;
  return { type, size: validSize };
}

/**
 * Render a component placeholder as React-PDF component
 * @param componentType - Component type with optional size (e.g., "RADAR:350")
 * @param errors
 * @param reportData - Optional report data for dynamic components (radar, table)
 */
function renderComponent(componentType: string, errors: string[], reportData?: ReportData): React.ReactElement {
  const { type, size } = parseComponentType(componentType);

  switch (type) {
    case "TABLE": {
      // If we have real data, render actual table
      if (reportData?.resultados?.empresa && reportData.resultados.empresa.length > 0) {
        const rows = reportData.resultados.empresa.map((result) =>
          React.createElement(
            View,
            {
              key: result.segment,
              style: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#e0e0e0", paddingVertical: 6 }
            },
            React.createElement(Text, { style: { flex: 2, fontSize: 10 } }, result.segment),
            React.createElement(
              Text,
              { style: { flex: 1, fontSize: 10, textAlign: "right" } },
              `${result.value.toFixed(1)}%`
            )
          )
        );

        return React.createElement(
          View,
          {
            style: {
              marginVertical: 15,
              padding: 12,
              backgroundColor: "#f9f9f9",
              borderWidth: 1,
              borderColor: "#e0e0e0",
              borderRadius: 4
            }
          },
          React.createElement(
            View,
            {
              style: {
                flexDirection: "row",
                borderBottomWidth: 2,
                borderBottomColor: "#333",
                paddingBottom: 6,
                marginBottom: 6
              }
            },
            React.createElement(Text, { style: { flex: 2, fontSize: 11, fontWeight: "bold" } }, "Dimensão"),
            React.createElement(
              Text,
              { style: { flex: 1, fontSize: 11, fontWeight: "bold", textAlign: "right" } },
              "Média"
            )
          ),
          ...rows
        );
      }

      // Fallback placeholder
      return React.createElement(
        View,
        { style: { marginVertical: 15, padding: 12, backgroundColor: "#f5f5f5", borderWidth: 1, borderColor: "#333" } },
        React.createElement(Text, { style: { fontSize: 11 } }, "[Tabela de Resultados - dados não disponíveis]")
      );
    }

    case "RADAR": {
      // Debug: Log what we received with actual values
      console.log("[RADAR] reportData received:", !!reportData);
      console.log("[RADAR] resultados.empresa:", reportData?.resultados?.empresa?.length ?? 0, "items");
      console.log("[RADAR] size:", size);

      // If we have real data, render actual radar chart using native react-pdf Svg
      if (reportData?.resultados?.empresa && reportData.resultados.empresa.length > 0) {
        return React.createElement(RadarChartPdf, {
          data: reportData.resultados.empresa,
          options: {
            showLabels: true,
            showValues: true,
            primaryColor: "#2563eb",
            ...(size && { width: size, height: size })
          }
        });
      }

      // Fallback placeholder when no data
      return React.createElement(
        Text,
        { style: componentStyles.placeholder },
        "[Gráfico Radar Empresa - dados não disponíveis]"
      );
    }

    case "RADAR_GERAL": {
      // Radar comparing empresa (primary) vs general average (secondary)
      console.log("[RADAR_GERAL] reportData received:", !!reportData);
      console.log("[RADAR_GERAL] resultados.geral:", reportData?.resultados?.geral?.length ?? 0, "items");
      console.log("[RADAR_GERAL] size:", size);

      if (
        reportData?.resultados?.empresa &&
        reportData?.resultados?.geral &&
        reportData.resultados.empresa.length > 0 &&
        reportData.resultados.geral.length > 0
      ) {
        // Combine empresa values with geral as comparison
        // value = empresa nota oficial, autoAvaliacao = média geral
        const combinedData = reportData.resultados.empresa.map((empresaItem, index) => {
          const geralItem = reportData.resultados.geral?.[index];
          return {
            segment: empresaItem.segment,
            value: empresaItem.value, // Nota oficial da empresa
            autoAvaliacao: geralItem?.value ?? 0 // Média geral para comparação
          };
        });

        return React.createElement(RadarChartPdf, {
          data: combinedData,
          options: {
            showLabels: true,
            showValues: true,
            primaryColor: "#2563eb", // Blue for empresa (nota oficial)
            secondaryColor: "#10b981", // Green for general average
            ...(size && { width: size, height: size })
          }
        });
      }

      // Fallback placeholder when no data
      return React.createElement(
        Text,
        { style: componentStyles.placeholder },
        "[Gráfico Radar Média Geral - dados não disponíveis]"
      );
    }

    case "RADAR_SEGMENTO": {
      // Radar comparing empresa (primary) vs segment/category average (secondary)
      console.log("[RADAR_SEGMENTO] reportData received:", !!reportData);
      console.log("[RADAR_SEGMENTO] resultados.categoria:", reportData?.resultados?.categoria?.length ?? 0, "items");
      console.log("[RADAR_SEGMENTO] size:", size);

      if (
        reportData?.resultados?.empresa &&
        reportData?.resultados?.categoria &&
        reportData.resultados.empresa.length > 0 &&
        reportData.resultados.categoria.length > 0
      ) {
        // Combine empresa values with categoria as comparison
        // value = empresa nota oficial, autoAvaliacao = média do segmento
        const combinedData = reportData.resultados.empresa.map((empresaItem, index) => {
          const categoriaItem = reportData.resultados.categoria?.[index];
          return {
            segment: empresaItem.segment,
            value: empresaItem.value, // Nota oficial da empresa
            autoAvaliacao: categoriaItem?.value ?? 0 // Média do segmento para comparação
          };
        });

        return React.createElement(RadarChartPdf, {
          data: combinedData,
          options: {
            showLabels: true,
            showValues: true,
            primaryColor: "#2563eb", // Blue for empresa (nota oficial)
            secondaryColor: "#f59e0b", // Orange for segment average
            ...(size && { width: size, height: size })
          }
        });
      }

      // Fallback placeholder when no data
      return React.createElement(
        Text,
        { style: componentStyles.placeholder },
        "[Gráfico Radar Média do Segmento - dados não disponíveis]"
      );
    }

    case "HEADER": {
      return React.createElement(
        View,
        { style: { marginBottom: 20, borderBottomWidth: 1, borderBottomColor: "#333", paddingBottom: 10 } },
        React.createElement(Text, { style: { fontSize: 14, fontWeight: "bold" } }, "Relatório de Diagnóstico"),
        React.createElement(Text, { style: { fontSize: 10, color: "#666" } }, "Template Preview")
      );
    }

    case "PAGE_NUMBER": {
      // React-PDF Text with render prop for dynamic page numbers
      return React.createElement(Text, {
        style: { fontSize: 10, color: "#666", textAlign: "center" },
        render: ({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
          `Pág ${pageNumber} de ${totalPages}`
      });
    }

    case "PAGE_BREAK": {
      return React.createElement(Text, { style: componentStyles.placeholder }, "[Quebra de Página]");
    }

    default: {
      errors.push(componentType);
      return React.createElement(
        Text,
        { style: componentStyles.placeholder },
        `[Componente não implementado: ${componentType}]`
      );
    }
  }
}

/**
 * Convert DOM node to React-PDF component
 * Handles text nodes that may contain component markers (MARKER_N_END)
 * Supports multiple markers in a single text node
 * @param text
 * @param errors
 * @param componentMap
 * @param reportData
 */
function resolveComponentFromText(
  text: string,
  errors: string[],
  componentMap: Map<string, React.ReactElement>,
  reportData?: ReportData
): React.ReactElement | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  // First, try exact match (fastest path)
  const mapped = componentMap.get(trimmed);
  if (mapped) return mapped;

  // Search for ALL marker patterns in the text (handles multiple markers in one node)
  const markerPattern = /MARKER_(\d+)_END/g;
  const matches = [...trimmed.matchAll(markerPattern)];

  if (matches.length > 0) {
    // If multiple markers found, create a View containing all components
    if (matches.length > 1) {
      const components: React.ReactElement[] = [];
      for (const match of matches) {
        const markerKey = match[0];
        const component = componentMap.get(markerKey);
        if (component) {
          components.push(component);
        }
      }
      if (components.length > 0) {
        return React.createElement(View, {}, ...components);
      }
    } else {
      // Single marker - return it directly
      const markerKey = matches[0]?.[0];
      if (markerKey) {
        const component = componentMap.get(markerKey);
        if (component) return component;
      }
    }
  }

  // Check for component placeholders that weren't pre-processed
  if (isComponentPlaceholder(trimmed)) {
    const componentType = extractComponentType(trimmed);
    if (componentType) {
      return renderComponent(componentType, errors, reportData);
    }
  }

  return null;
}

/**
 *
 * @param children
 */
function hasBlockChildren(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => React.isValidElement(child) && child.type === View);
}

/**
 * Create a View with background image using absolute positioning
 * For non-fullpage elements only (fullpage backgrounds are handled by TemplatePreview)
 * @param backgroundUrl - URL of the background image
 * @param styles - Additional styles for the container
 * @param children - Content to render on top of the image
 * @param fullPage - If true, returns just children (background handled externally)
 */
function createViewWithBackground(
  backgroundUrl: string,
  styles: Style,
  children: React.ReactNode,
  fullPage = false
): React.ReactElement {
  // For fullpage elements, return children directly without extra wrapper
  // The background image and container are handled by TemplatePreview at the Page level
  if (fullPage) {
    // Return a simple fragment-like View with no extra styling
    // The children ARE the content to show on top of the background
    return React.createElement(View, {}, children);
  }

  // Regular mode: background within container bounds
  const containerStyle: Style = {
    ...styles,
    position: "relative"
  };

  const imageStyle: Style = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const
  };

  const contentStyle: Style = {
    position: "relative",
    zIndex: 1
  };

  return React.createElement(
    View,
    { style: containerStyle },
    React.createElement(Image, { src: backgroundUrl, style: imageStyle }),
    React.createElement(View, { style: contentStyle }, children)
  );
}

/**
 *
 * @param node
 * @param errors
 * @param componentMap
 * @param reportData
 */
function convertNodeToComponent(
  node: Element,
  errors: string[],
  componentMap: Map<string, React.ReactElement>,
  reportData?: ReportData
): React.ReactElement {
  // Cast children to DOMNode[] - safe because Element.children are valid DOM nodes
  const children = domToReact(node.children as DOMNode[], {
    replace: (childNode) => {
      if (childNode.type === "tag") {
        return convertNodeToComponent(childNode, errors, componentMap, reportData);
      }
      if (childNode.type === "text") {
        const text = childNode.data;
        // Skip whitespace-only text nodes (they cause "Invalid string child" warnings)
        if (!text.trim()) return null;
        const component = resolveComponentFromText(text, errors, componentMap, reportData);
        if (component) return component;
        return React.createElement(Text, {}, text.trim());
      }
      return null;
    }
  });

  // Parse styles with background image extraction
  const { styles: parsedStyles, backgroundImage } = parseStylesWithBackground(node.attribs?.style);

  // Check for fullpage attribute (data-fullpage="true")
  const isFullPage = node.attribs?.["data-fullpage"] === "true";

  // Map HTML tag to React-PDF component
  switch (node.name) {
    case "div":
    case "section":
    case "article": {
      // If there's a background image, use special rendering
      if (backgroundImage) {
        return createViewWithBackground(backgroundImage, parsedStyles, children, isFullPage);
      }
      return React.createElement(View, { style: parsedStyles }, children);
    }

    case "p": {
      if (hasBlockChildren(children)) {
        return React.createElement(View, { style: { marginBottom: 10, ...parsedStyles } }, children);
      }
      return React.createElement(Text, { style: { marginBottom: 10, ...parsedStyles } }, children);
    }

    case "h1": {
      return React.createElement(
        Text,
        { style: { fontSize: 24, fontWeight: "bold", marginBottom: 10, ...parsedStyles } },
        children
      );
    }

    case "h2": {
      return React.createElement(
        Text,
        { style: { fontSize: 20, fontWeight: "bold", marginBottom: 8, ...parsedStyles } },
        children
      );
    }

    case "h3": {
      return React.createElement(
        Text,
        { style: { fontSize: 16, fontWeight: "bold", marginBottom: 6, ...parsedStyles } },
        children
      );
    }

    case "strong":
    case "b": {
      return React.createElement(Text, { style: { fontWeight: "bold", ...parsedStyles } }, children);
    }

    case "em":
    case "i": {
      return React.createElement(Text, { style: { fontStyle: "italic", ...parsedStyles } }, children);
    }

    case "br": {
      return React.createElement(Text, {}, "\n");
    }

    case "span": {
      return React.createElement(Text, { style: parsedStyles }, children);
    }

    case "img": {
      const source = node.attribs?.src;
      if (!source) {
        errors.push("Tag <img> sem atributo src");
        return React.createElement(View, {});
      }
      // Get dimensions from attributes or styles
      const imgStyle: Style = { ...parsedStyles };
      if (node.attribs?.width) {
        const widthValue = Number.parseInt(node.attribs.width, 10);
        if (!Number.isNaN(widthValue)) {
          imgStyle.width = widthValue;
        }
      }
      if (node.attribs?.height) {
        const heightValue = Number.parseInt(node.attribs.height, 10);
        if (!Number.isNaN(heightValue)) {
          imgStyle.height = heightValue;
        }
      }
      return React.createElement(Image, { src: source, style: imgStyle });
    }

    default: {
      // For unknown tags, render children in a View with styles
      return React.createElement(View, { style: parsedStyles }, children);
    }
  }
}

/**
 * Convert HTML string to React-PDF components
 * @param html
 */
/**
 * Find the closing tag position for a given opening tag, handling nested tags
 * @param html
 * @param tagName
 * @param startAfterOpeningTag
 */
function findClosingTagPosition(html: string, tagName: string, startAfterOpeningTag: number): number {
  let depth = 1;
  let pos = startAfterOpeningTag;

  const openPattern = new RegExp(`<${tagName}(?:\\s|>)`, "gi");
  const closePattern = new RegExp(`</${tagName}>`, "gi");

  while (depth > 0 && pos < html.length) {
    openPattern.lastIndex = pos;
    closePattern.lastIndex = pos;

    const openMatch = openPattern.exec(html);
    const closeMatch = closePattern.exec(html);

    if (!closeMatch) {
      // No closing tag found, return end of string
      return html.length;
    }

    if (openMatch && openMatch.index < closeMatch.index) {
      // Found another opening tag before the closing tag
      depth++;
      pos = openMatch.index + openMatch[0].length;
    } else {
      // Found closing tag
      depth--;
      if (depth === 0) {
        return closeMatch.index + closeMatch[0].length;
      }
      pos = closeMatch.index + closeMatch[0].length;
    }
  }

  return html.length;
}

/**
 * Split HTML into sections based on data-fullpage attribute
 * Returns array of { html, isFullPage, backgroundImage }
 * @param html
 */
function splitHtmlIntoPages(html: string): Array<{ html: string; isFullPage: boolean; backgroundImage?: string }> {
  const pages: Array<{ html: string; isFullPage: boolean; backgroundImage?: string }> = [];

  // Pattern to find opening tags with data-fullpage="true"
  const fullpageOpenPattern = /<(div|section|article)\s+[^>]*data-fullpage="true"[^>]*>/gi;

  let lastIndex = 0;
  let match;

  while ((match = fullpageOpenPattern.exec(html)) !== null) {
    // Content before this fullpage element (regular content)
    const beforeContent = html.slice(lastIndex, match.index).trim();
    if (beforeContent) {
      pages.push({ html: beforeContent, isFullPage: false });
    }

    // Find the proper closing tag
    const tagName = match[1] ?? "div";
    const openingTagEnd = match.index + match[0].length;
    const closingTagEnd = findClosingTagPosition(html, tagName, openingTagEnd);

    // Extract the full element
    const fullpageHtml = html.slice(match.index, closingTagEnd);

    // Extract background-image from style attribute
    const styleMatch = match[0].match(/style="([^"]*)"/);
    let backgroundImage: string | undefined;
    if (styleMatch) {
      const { backgroundImage: bg } = parseStylesWithBackground(styleMatch[1]);
      backgroundImage = bg;
    }

    pages.push({ html: fullpageHtml, isFullPage: true, backgroundImage });

    lastIndex = closingTagEnd;
    // Update regex position to continue after this element
    fullpageOpenPattern.lastIndex = closingTagEnd;
  }

  // Remaining content after last fullpage element
  const afterContent = html.slice(lastIndex).trim();
  if (afterContent) {
    pages.push({ html: afterContent, isFullPage: false });
  }

  return pages;
}

/**
 * Options for HTML to React-PDF conversion
 */
export interface HtmlToReactPdfOptions {
  /** Report data for dynamic components (radar, table) */
  reportData?: ReportData;
}

/**
 *
 * @param html
 * @param options
 */
export function htmlToReactPDF(html: string, options: HtmlToReactPdfOptions = {}): ConversionResult {
  const { reportData } = options;
  const errors: string[] = [];

  // Pre-process: Replace component placeholders with markers
  let processed = html;
  const componentMap = new Map<string, React.ReactElement>();
  let counter = 0;

  // Find component placeholders: {{COMPONENT:TYPE}}
  console.log("[htmlToReactPDF] Input HTML preview:", html.slice(0, 500));
  console.log("[htmlToReactPDF] Has reportData:", !!reportData);

  const matches = html.match(/{{COMPONENT:([^}]+)}}/g);
  console.log("[htmlToReactPDF] Component matches found:", matches);

  if (matches) {
    for (const fullMatch of matches) {
      const typeMatch = fullMatch.match(/COMPONENT:([^}]+)/);
      if (typeMatch && typeMatch[1]) {
        const componentType = typeMatch[1];
        console.log("[htmlToReactPDF] Processing component:", componentType);
        const marker = `MARKER_${counter}_END`;
        componentMap.set(marker, renderComponent(componentType, errors, reportData));
        processed = processed.replace(fullMatch, marker);
        counter++;
      }
    }
  }

  try {
    // Check if we have fullpage elements
    const hasFullPageElements = /data-fullpage="true"/i.test(processed);

    // If we have fullpage elements, split into pages
    if (hasFullPageElements) {
      const htmlSections = splitHtmlIntoPages(processed);
      const pages: PageContent[] = [];

      for (const section of htmlSections) {
        const options = {
          replace: (domNode: DOMNode) => {
            if (domNode.type === "text") {
              const text = domNode.data;
              // Skip whitespace-only text nodes (they cause "Invalid string child" warnings)
              if (!text.trim()) return null;
              const component = resolveComponentFromText(text, errors, componentMap, reportData);
              if (component) return component;
              return React.createElement(Text, {}, text.trim());
            }
            if (domNode.type === "tag" && domNode.attribs) {
              return convertNodeToComponent(domNode, errors, componentMap, reportData);
            }
            return null;
          }
        };

        const parsed = parse(section.html, options);
        const component = Array.isArray(parsed)
          ? React.createElement(View, {}, ...parsed)
          : (parsed as React.ReactElement);

        pages.push({
          component,
          isFullPage: section.isFullPage,
          backgroundImage: section.backgroundImage
        });
      }

      return {
        component: null,
        errors,
        pages
      };
    }

    // No fullpage elements - use legacy single-page rendering
    const options = {
      replace: (domNode: DOMNode) => {
        if (domNode.type === "text") {
          const text = domNode.data;
          // Skip whitespace-only text nodes (they cause "Invalid string child" warnings)
          if (!text.trim()) return null;
          const component = resolveComponentFromText(text, errors, componentMap, reportData);
          if (component) return component;
          return React.createElement(Text, {}, text.trim());
        }
        if (domNode.type === "tag" && domNode.attribs) {
          return convertNodeToComponent(domNode, errors, componentMap, reportData);
        }
        return null;
      }
    };

    const parsed = parse(processed, options);
    const component = Array.isArray(parsed) ? React.createElement(View, {}, ...parsed) : (parsed as React.ReactElement);

    return {
      component,
      errors
    };
  } catch (error) {
    errors.push(`Failed to convert HTML: ${error instanceof Error ? error.message : String(error)}`);
    return {
      component: null,
      errors
    };
  }
}

/**
 * Extract URL from CSS url() function
 * @param value - CSS value like "url('https://...')" or "url(https://...)"
 * @returns The URL string or null if not a url() value
 */
function extractUrlFromCss(value: string): string | null {
  const match = value.match(/url\s*\(\s*["']?([^\s"')]+)["']?\s*\)/);
  return match?.[1] ?? null;
}

/**
 * CSS properties NOT supported by react-pdf that should be filtered out
 * These can cause "unsupported number: Infinity" or other errors
 */
const UNSUPPORTED_CSS_PROPERTIES = new Set([
  // Shadow and filter effects
  "text-shadow",
  "box-shadow",
  "filter",
  "backdrop-filter",
  // Transforms and animations
  "transform",
  "transition",
  "animation",
  "animation-name",
  "animation-duration",
  "animation-timing-function",
  "animation-delay",
  "animation-iteration-count",
  "animation-direction",
  "animation-fill-mode",
  "animation-play-state",
  // UI properties
  "cursor",
  "outline",
  "outline-width",
  "outline-style",
  "outline-color",
  "outline-offset",
  "user-select",
  "pointer-events",
  "resize",
  "appearance",
  // Clipping and masking
  "clip-path",
  "mask",
  "mask-image",
  // 3D transforms
  "perspective",
  "perspective-origin",
  "transform-origin",
  "transform-style",
  "backface-visibility",
  // Performance hints
  "will-change",
  // Scroll behavior
  "scroll-behavior",
  "scroll-snap-type",
  "scroll-snap-align",
  "overscroll-behavior",
  "touch-action",
  // Content generation
  "content",
  "quotes",
  "counter-reset",
  "counter-increment",
  "list-style-image",
  // Grid layout (NOT supported by react-pdf)
  "grid-template-columns",
  "grid-template-rows",
  "grid-template-areas",
  "grid-template",
  "grid-auto-columns",
  "grid-auto-rows",
  "grid-auto-flow",
  "grid-area",
  "grid-column",
  "grid-row",
  "grid-gap",
  "gap",
  "row-gap",
  "column-gap",
  // Multi-column layout
  "column-count",
  "column-gap",
  "column-rule",
  "column-span",
  "column-width",
  // Text properties not supported
  "text-transform",
  "text-decoration-style",
  "text-decoration-color",
  "text-overflow",
  "white-space",
  "word-break",
  "word-wrap",
  "overflow-wrap",
  "hyphens",
  // Other unsupported
  "object-position",
  "isolation",
  "mix-blend-mode",
  "background-blend-mode"
]);

/**
 * Values that indicate unsupported CSS features
 */
const UNSUPPORTED_VALUE_PATTERNS = [
  /linear-gradient\s*\(/i,
  /radial-gradient\s*\(/i,
  /conic-gradient\s*\(/i,
  /repeating-(linear|radial|conic)-gradient\s*\(/i,
  /calc\s*\(/i,
  /var\s*\(/i,
  /clamp\s*\(/i,
  /min\s*\(/i,
  /max\s*\(/i,
  /repeat\s*\(/i, // grid repeat()
  /minmax\s*\(/i, // grid minmax()
  /fit-content\s*\(/i,
  /\d+fr\b/i // grid fractional units (1fr, 2fr, etc.)
];

/**
 * Parse a CSS numeric value, returning undefined if invalid
 * Handles px, pt, %, em, rem, etc.
 * @param value
 */
function parseCssNumericValue(value: string): string | number | undefined {
  const trimmed = value.trim();

  // Check for auto, inherit, etc.
  if (["auto", "inherit", "initial", "unset", "none"].includes(trimmed)) {
    return trimmed;
  }

  // Check for percentage
  if (trimmed.endsWith("%")) {
    return trimmed; // react-pdf accepts percentage strings
  }

  // Check for numeric with unit
  const numericMatch = trimmed.match(/^(-?\d*\.?\d+)(px|pt|em|rem|vh|vw)?$/);
  if (numericMatch) {
    const number_ = Number.parseFloat(numericMatch[1] ?? "0");
    if (!Number.isFinite(number_)) return undefined;
    // react-pdf prefers numbers for px values
    return number_;
  }

  // Return as-is for other valid values (colors, etc.)
  return trimmed;
}

/**
 * Parse inline CSS styles to React-PDF style object
 * Extracts background-image separately since react-pdf doesn't support it natively
 * Filters out unsupported CSS properties that could cause errors
 * @param styleString
 */
function parseStylesWithBackground(styleString?: string): ParsedStyleResult {
  if (!styleString) return { styles: {} };

  const styles: Record<string, string | number> = {};
  let backgroundImage: string | undefined;
  const declarations = styleString.split(";");

  for (const declaration of declarations) {
    const colonIndex = declaration.indexOf(":");
    if (colonIndex === -1) continue;

    const property = declaration.slice(0, colonIndex).trim().toLowerCase();
    const value = declaration.slice(colonIndex + 1).trim();
    if (!property || !value) continue;

    // Skip unsupported CSS properties
    if (UNSUPPORTED_CSS_PROPERTIES.has(property)) {
      continue;
    }

    // Handle background-image specially
    if (property === "background-image") {
      const url = extractUrlFromCss(value);
      if (url) {
        backgroundImage = url;
      }
      continue; // Don't add to styles - react-pdf doesn't support it
    }

    // Skip background shorthand with url (e.g., "background: url(...) no-repeat")
    if (property === "background" && value.includes("url(")) {
      const url = extractUrlFromCss(value);
      if (url) {
        backgroundImage = url;
      }
      continue;
    }

    // Skip values with unsupported CSS functions/patterns
    // This catches linear-gradient, calc(), var(), grid units (1fr), etc.
    const hasUnsupportedValue = UNSUPPORTED_VALUE_PATTERNS.some((pattern) => pattern.test(value));
    if (hasUnsupportedValue) {
      continue;
    }

    // Skip rgba() in non-color properties
    if (
      /rgba?\s*\(/.test(value) &&
      !["color", "background-color", "background", "border-color", "fill", "stroke"].includes(property)
    ) {
      continue;
    }

    // Convert CSS property to camelCase
    const camelProperty = property.replaceAll(/-([a-z])/g, (_, char: string) => char.toUpperCase());

    // Handle display property specially - convert grid to flex (best approximation)
    if (camelProperty === "display") {
      if (value === "grid") {
        styles[camelProperty] = "flex";
        styles["flexWrap"] = "wrap";
        continue;
      }
      // Only allow supported display values
      if (!["flex", "none"].includes(value)) {
        continue; // Skip unsupported display values like 'block', 'inline-block'
      }
    }

    // Handle flex shorthand - convert to flexGrow (react-pdf expects number)
    if (camelProperty === "flex") {
      const flexValue = Number.parseFloat(value);
      if (Number.isFinite(flexValue)) {
        styles["flexGrow"] = flexValue;
      }
      continue;
    }

    // Handle justify-content - react-pdf has limited support
    if (camelProperty === "justifyContent") {
      const supportedJustify = ["flex-start", "flex-end", "center", "space-between", "space-around"];
      if (supportedJustify.includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle align-items - react-pdf has limited support
    if (camelProperty === "alignItems") {
      const supportedAlign = ["flex-start", "flex-end", "center", "stretch", "baseline"];
      if (supportedAlign.includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle position - only relative and absolute supported
    if (camelProperty === "position") {
      if (["relative", "absolute"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle flexDirection
    if (camelProperty === "flexDirection") {
      if (["row", "column", "row-reverse", "column-reverse"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle flexWrap
    if (camelProperty === "flexWrap") {
      if (["nowrap", "wrap", "wrap-reverse"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle font-weight - react-pdf expects specific values
    if (camelProperty === "fontWeight") {
      if (["bold", "normal", "100", "200", "300", "400", "500", "600", "700", "800", "900"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle font-style
    if (camelProperty === "fontStyle") {
      if (["normal", "italic", "oblique"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle text-align
    if (camelProperty === "textAlign") {
      if (["left", "right", "center", "justify"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle text-decoration
    if (camelProperty === "textDecoration") {
      if (["none", "underline", "line-through"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle overflow
    if (camelProperty === "overflow") {
      if (["visible", "hidden"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle object-fit for images
    if (camelProperty === "objectFit") {
      if (["contain", "cover", "fill", "none", "scale-down"].includes(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // For numeric properties, parse and validate
    const numericProperties = [
      "width",
      "height",
      "top",
      "left",
      "right",
      "bottom",
      "margin",
      "marginTop",
      "marginRight",
      "marginBottom",
      "marginLeft",
      "padding",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "fontSize",
      "lineHeight",
      "borderWidth",
      "borderRadius",
      "minWidth",
      "maxWidth",
      "minHeight",
      "maxHeight",
      "flexBasis",
      "flexGrow",
      "flexShrink",
      "zIndex",
      "opacity",
      "borderTopWidth",
      "borderRightWidth",
      "borderBottomWidth",
      "borderLeftWidth",
      "borderTopLeftRadius",
      "borderTopRightRadius",
      "borderBottomLeftRadius",
      "borderBottomRightRadius",
      "paddingHorizontal",
      "paddingVertical",
      "marginHorizontal",
      "marginVertical"
    ];

    if (numericProperties.includes(camelProperty)) {
      const parsed = parseCssNumericValue(value);
      if (parsed !== undefined) {
        styles[camelProperty] = parsed;
      }
      // Skip invalid numeric values (they could cause Infinity)
      continue;
    }

    // Handle color properties - allow color values (hex, rgb, rgba, named colors)
    const colorProperties = [
      "color",
      "backgroundColor",
      "borderColor",
      "borderTopColor",
      "borderRightColor",
      "borderBottomColor",
      "borderLeftColor"
    ];
    if (colorProperties.includes(camelProperty)) {
      // Only allow valid color formats
      if (/^(#[\dA-Fa-f]{3,8}|rgba?\s*\(|[A-Za-z]+)/.test(value)) {
        styles[camelProperty] = value;
      }
      continue;
    }

    // Handle font-family - React-PDF supports: Courier, Helvetica, Times-Roman
    if (camelProperty === "fontFamily") {
      // Extract first font name, removing quotes
      const firstFont = value.split(",")[0]?.trim().replaceAll(/["']/g, "") ?? "Helvetica";
      const fontLower = firstFont.toLowerCase();
      if (fontLower.includes("courier") || fontLower === "monospace") {
        styles[camelProperty] = "Courier";
      } else if (fontLower.includes("times") || fontLower === "serif") {
        styles[camelProperty] = "Times-Roman";
      } else {
        styles[camelProperty] = "Helvetica"; // Default fallback
      }
      continue;
    }

    // Skip any remaining unknown properties to be safe
    // This prevents unknown CSS from potentially causing Infinity errors
  }

  return { styles: styles as PDFStyles, backgroundImage };
}

/**
 * Handle component placeholders (radar, table, etc)
 * @param text
 */
export function isComponentPlaceholder(text: string): boolean {
  return /^{{COMPONENT:/.test(text);
}

/**
 * Extract component type from placeholder
 * @param placeholder
 */
export function extractComponentType(placeholder: string): string | null {
  const match = placeholder.match(/^{{COMPONENT:([^:}]+)/);
  return match?.[1] ?? null;
}
